import { resolveItemModelId } from '@sorrem/shared';
import type { UICoalescence, UIInventoryItem } from '@sorrem-client/types';
import { CrystalIcon } from '../CrystalIcon';
import { ItemIcon } from '../ItemIcon';
import { UpgradePips } from '../UpgradePips';
import { CRYSTAL_TYPE_COLORS } from '@sorrem-client/ui/shared/crystal-type-colors';
import { CoalescingProgress } from './components/CoalescingProgress';
import styles from './CarriedSlotContent.module.scss';

interface CarriedSlotContentProps {
    item: UIInventoryItem | undefined;
    /** Coalescences headed for this slot, soonest first. */
    pending: readonly UICoalescence[];
}

/**
 * What a carried slot shows: a crystal stack and its count, a piece of
 * equipment (with its upgrade pips once upgraded, #287), and a ghost of any
 * crystal coalescing into the slot with its progress. The item's tier and
 * how it compares with what is worn are on the frame (`ItemSlotMarks`).
 */
export function CarriedSlotContent({ item, pending }: CarriedSlotContentProps): JSX.Element | null {
    const next = pending[0];
    if (item === undefined && next === undefined) {
        return null;
    }

    return (
        <span className={styles.carriedSlotContent}>
            {item?.crystal !== undefined && (
                <CrystalIcon crystalType={item.crystal.crystalType} size={item.crystal.size} className={styles.crystal} />
            )}
            {item !== undefined && item.crystal === undefined && (
                <ItemIcon modelId={resolveItemModelId(item)} className={styles.itemIcon} />
            )}
            {item === undefined && next !== undefined && (
                <CrystalIcon crystalType={next.crystalType} size={next.size} ghost className={styles.crystal} />
            )}
            {item?.crystal !== undefined && <span className={styles.quantity}>{item.qty}</span>}
            {(item?.instance?.upgradeLevel ?? 0) > 0 && <UpgradePips level={item?.instance?.upgradeLevel ?? 0} className={styles.pips} />}
            {next !== undefined && (
                <CoalescingProgress
                    key={next.id}
                    startedAtMs={next.startedAtMs}
                    completesAtMs={next.completesAtMs}
                    color={CRYSTAL_TYPE_COLORS[next.crystalType]}
                />
            )}
        </span>
    );
}
